import React from 'react';

import {
  faSort,
  faSortDown,
  faSortUp,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';

export const TABLE_TITLE_CLASSES =
  'vai-flex vai-items-center vai-justify-between vai-text-sm vai-font-medium vai-text-muted-foreground';

export interface TableColumn<T> {
  key: string;
  header: React.ReactNode;
  width?: string;
  isSortable?: boolean;
  render: (item: T, index: number) => React.ReactNode;
}

export interface SortableTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  className?: string;
  isSorting?: boolean;
  rowKey?: (item: T, index: number) => string;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  scrollRef?: React.Ref<HTMLDivElement>;
  sentinelRef?: React.Ref<HTMLDivElement>;
  nextAppointmentRef?: React.Ref<HTMLTableRowElement>;
}

/**
 * Generic sortable table component that can display any type of data
 * Includes rounded corners, white background, and border styling
 *
 * @template T - The type of data items in the table (e.g., Appointment, User, etc.)
 */
export const SortableTable = <T extends unknown>({
  data,
  columns,
  sortField,
  sortDirection,
  className,
  isSorting,
  rowKey,
  onSort,
  scrollRef,
  sentinelRef,
  nextAppointmentRef,
}: SortableTableProps<T>) => {
  const handleSort = (field: string) => {
    if (!onSort) return;

    const newDirection =
      sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(field, newDirection);
  };

  return (
    <div
      className={cn(
        'vai-rounded-lg vai-border vai-border-grey-200 vai-bg-white vai-overflow-hidden vai-flex-1',
        className,
      )}
    >
      <div className="vai-overflow-auto vai-h-full" ref={scrollRef}>
        <Table className="vai-w-full">
          <TableHeader className="vai-sticky vai-top-0 vai-bg-white vai-z-10">
            <TableRow className="vai-rounded-t-lg">
              {columns.map((column) => (
                <TableHead
                  key={String(column.key)}
                  className={cn(
                    column.width || '',
                    column.isSortable
                      ? 'vai-cursor-pointer hover:vai-bg-gray-50 vai-transition-colors vai-group'
                      : '',
                  )}
                  onClick={() => column.isSortable && handleSort(column.key)}
                >
                  <div className={TABLE_TITLE_CLASSES}>
                    {column.header}
                    {column.isSortable &&
                      (() => {
                        const isCurrentColumn = sortField === column.key;
                        const isCurrentlySorting = isSorting && isCurrentColumn;

                        let sortIcon;
                        let sortIconClasses;

                        if (isCurrentlySorting) {
                          sortIcon = faSpinner;
                          sortIconClasses =
                            'vai-text-blue-600 vai-opacity-100 vai-animate-spin';
                        } else if (isCurrentColumn) {
                          sortIcon =
                            sortDirection === 'asc' ? faSortUp : faSortDown;
                          sortIconClasses = 'vai-text-blue-600 vai-opacity-100';
                        } else {
                          sortIcon = faSort;
                          sortIconClasses =
                            'vai-text-gray-400 vai-opacity-60 group-hover:vai-opacity-80';
                        }

                        return (
                          <FontAwesomeIcon
                            icon={sortIcon}
                            className={sortIconClasses}
                          />
                        );
                      })()}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                ref={index === data.length - 1 ? nextAppointmentRef : undefined}
                key={rowKey ? rowKey(item, index) : index}
                className={index === data.length - 1 ? 'vai-rounded-b-lg' : ''}
              >
                {columns.map((column) => (
                  <TableCell key={String(column.key)}>
                    {column.render(item, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {/* Infinite scroll trigger */}
        {sentinelRef && <div ref={sentinelRef} className="vai-h-4" />}
      </div>
    </div>
  );
};
